import { motionStill } from "@/utils/motion";

export interface BeforeAfterConfig {
  beforeUrl: string;
  beforeAlt?: string;
  afterUrl: string;
  afterAlt?: string;
  /** Per cent of the width that shows the before photo at first, 0–100; 50 by default. */
  startPosition?: number;
  beforeLabel?: string;
  afterLabel?: string;
  /** »mobile | desktop« or one ratio for both, such as 3 / 2 (the default). */
  aspectRatio?: string;
}

export interface BeforeAfter {
  beforeUrl: string;
  beforeAlt?: string;
  afterUrl: string;
  afterAlt?: string;
  start: number;
  beforeLabel: string;
  afterLabel: string;
  ratio: string;
  ratioDesktop: string;
}

export interface BeforeAfterLabels {
  start: number;
  beforeLabel: string;
  afterLabel: string;
}

const DEFAULT_RATIO = "3 / 2";
const RATIO = /^\d+(\.\d+)?\s*\/\s*\d+(\.\d+)?$/;
const KEY_STEP = 5;
const PAGE_STEP = 25;

const text = (value: unknown) =>
  typeof value === "string" && value.trim() ? value.trim() : undefined;

const clamp = (value: number) => Math.min(100, Math.max(0, value));

function startOf(value: unknown) {
  const number =
    typeof value === "string" && value.trim() ? Number(value) : value;
  return typeof number === "number" && Number.isFinite(number)
    ? clamp(number)
    : 50;
}

/** A stored `aspectRatio` as [phone, from 768 px]; an unreadable one is 3 / 2. */
export function ratiosOf(value: unknown): [string, string] {
  const [mobile, desktop = mobile] = (typeof value === "string" ? value : "")
    .split("|")
    .map((part) => part.trim());
  const ratio = RATIO.test(mobile) ? mobile : DEFAULT_RATIO;
  return [ratio, RATIO.test(desktop) ? desktop : ratio];
}

/** The slider a `BeforeAfterComponent`'s config describes; undefined without both photos. */
export function resolveBeforeAfter(value: unknown): BeforeAfter | undefined {
  const config = (value && typeof value === "object" ? value : {}) as Record<
    keyof BeforeAfterConfig,
    unknown
  >;
  const beforeUrl = text(config.beforeUrl);
  const afterUrl = text(config.afterUrl);
  if (!beforeUrl || !afterUrl) return undefined;
  const [ratio, ratioDesktop] = ratiosOf(config.aspectRatio);
  return {
    beforeUrl,
    beforeAlt: text(config.beforeAlt),
    afterUrl,
    afterAlt: text(config.afterAlt),
    start: startOf(config.startPosition),
    beforeLabel: text(config.beforeLabel) ?? "Før",
    afterLabel: text(config.afterLabel) ?? "Efter",
    ratio,
    ratioDesktop,
  };
}

/** Where `key` moves the divider from `position` (the per cent of the width that shows the before photo), as a WAI-ARIA
 *  slider moves; undefined for a key the slider leaves alone. */
export function beforeAfterKey(
  key: string,
  position: number,
): number | undefined {
  const moved = (by: number) => clamp(Math.round((position + by) * 10) / 10);
  switch (key) {
    case "ArrowRight":
    case "ArrowUp":
      return moved(KEY_STEP);
    case "ArrowLeft":
    case "ArrowDown":
      return moved(-KEY_STEP);
    case "PageUp":
      return moved(PAGE_STEP);
    case "PageDown":
      return moved(-PAGE_STEP);
    case "Home":
      return 0;
    case "End":
      return 100;
    default:
      return undefined;
  }
}

export function positionAt(
  clientX: number,
  rect: { left: number; width: number },
): number {
  if (!(rect.width > 0)) return 50;
  return clamp(Math.round(((clientX - rect.left) / rect.width) * 1000) / 10);
}

export const valueText = (
  position: number,
  beforeLabel: string,
  afterLabel: string,
) =>
  `${beforeLabel} ${Math.round(position)} %, ${afterLabel} ${100 - Math.round(position)} %`;

/**
 * Makes the server-rendered slider in `figure` move. A mouse press puts the divider where it is; a finger or a pen moves
 * it only once it drags sideways, since before/after.css leaves a vertical swipe to the page, which then cancels the
 * pointer. A key or a press glides the divider unless motionStill(); a drag follows the pointer exactly.
 */
export function attachBeforeAfter(
  figure: HTMLElement,
  { start, beforeLabel, afterLabel }: BeforeAfterLabels,
): () => void {
  const handle = figure.querySelector<HTMLElement>(
    "[data-re-before-after-handle]",
  );
  if (!handle) return () => {};
  let position = start;
  let pointer: number | undefined;
  const set = (next: number, glide: boolean) => {
    position = next;
    figure.toggleAttribute(
      "data-re-before-after-glide",
      glide && !motionStill(figure),
    );
    figure.style.setProperty("--re-before-after-position", `${next}%`);
    handle.setAttribute("aria-valuenow", String(Math.round(next)));
    handle.setAttribute(
      "aria-valuetext",
      valueText(next, beforeLabel, afterLabel),
    );
  };
  const at = (event: PointerEvent) =>
    positionAt(event.clientX, figure.getBoundingClientRect());
  const keydown = (event: KeyboardEvent) => {
    const next = beforeAfterKey(event.key, position);
    if (next === undefined) return;
    event.preventDefault();
    set(next, true);
  };
  const down = (event: PointerEvent) => {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    pointer = event.pointerId;
    figure.setPointerCapture?.(event.pointerId);
    figure.toggleAttribute("data-re-before-after-dragging", true);
    handle.focus({ preventScroll: true });
    if (event.pointerType === "mouse") {
      // The mousedown that follows would otherwise move focus off the handle to <body>, and the arrow keys would scroll.
      event.preventDefault();
      set(at(event), true);
    }
  };
  const move = (event: PointerEvent) => {
    if (event.pointerId === pointer) set(at(event), false);
  };
  const up = (event: PointerEvent) => {
    if (event.pointerId !== pointer) return;
    pointer = undefined;
    figure.toggleAttribute("data-re-before-after-dragging", false);
  };
  const pointerListeners: [string, (event: PointerEvent) => void][] = [
    ["pointerdown", down],
    ["pointermove", move],
    ["pointerup", up],
    ["pointercancel", up],
    ["lostpointercapture", up],
  ];
  handle.addEventListener("keydown", keydown);
  for (const [type, listener] of pointerListeners)
    figure.addEventListener(type, listener as EventListener);
  return () => {
    handle.removeEventListener("keydown", keydown);
    for (const [type, listener] of pointerListeners)
      figure.removeEventListener(type, listener as EventListener);
  };
}
